import React, { useEffect, useState } from "react";
import { collection, deleteDoc, doc, getDocs } from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";
import { db, storage } from "../../firebase";

const TEXTO_CONFIRMACION = "REINICIAR MITICO";
const COLECCIONES_A_LIMPIAR = [
  {
    nombre: "productos",
    etiqueta: "Productos",
    borrarImagenes: true,
    camposImagen: ["imagen"],
  },
  {
    nombre: "categorias",
    etiqueta: "Categorías / Orden del menú",
    borrarImagenes: false,
    camposImagen: [],
  },
  {
    nombre: "pedidos",
    etiqueta: "Pedidos / Analíticas",
    borrarImagenes: false,
    camposImagen: [],
  },
  {
    nombre: "promociones",
    etiqueta: "Promociones",
    borrarImagenes: false,
    camposImagen: [],
  },
  {
    nombre: "marketing",
    etiqueta: "Marketing / Banners",
    borrarImagenes: true,
    camposImagen: ["url", "imagen", "imagenUrl"],
  },
  {
    nombre: "especiales",
    etiqueta: "Especiales del Mes",
    borrarImagenes: true,
    camposImagen: ["url", "imagen", "imagenUrl"],
  },
];

const CLAVES_LOCAL_STORAGE = [
  "carritoMitico",
  "mitico_favoritos_productos",
  "mitico_ultimo_pedido_productos",
];

const esUrlImagenStorage = (url) => {
  return (
    typeof url === "string" &&
    url.trim() !== "" &&
    url.includes("firebasestorage.googleapis.com")
  );
};

const obtenerDocsColeccion = async (nombreColeccion) => {
  const snapshot = await getDocs(collection(db, nombreColeccion));

  return snapshot.docs.map((documento) => ({
    id: documento.id,
    data: documento.data(),
  }));
};

const extraerImagenesDeDocs = (docs = [], camposImagen = []) => {
  const imagenes = [];

  docs.forEach((item) => {
    camposImagen.forEach((campo) => {
      const posibleUrl = item.data?.[campo];

      if (esUrlImagenStorage(posibleUrl)) {
        imagenes.push(posibleUrl);
      }
    });
  });

  return imagenes;
};

const borrarImagenStorage = async (urlImagen) => {
  try {
    const imagenRef = ref(storage, urlImagen);
    await deleteObject(imagenRef);

    return {
      ok: true,
      url: urlImagen,
    };
  } catch (error) {
    console.warn("No se pudo borrar imagen de Storage:", urlImagen, error);

    return {
      ok: false,
      url: urlImagen,
      error: error?.code || "Error desconocido",
    };
  }
};

const borrarDocsFirestore = async (nombreColeccion, docs = []) => {
  const resultados = await Promise.allSettled(
    docs.map((item) => deleteDoc(doc(db, nombreColeccion, item.id))),
  );

  return {
    total: docs.length,
    borrados: resultados.filter((resultado) => resultado.status === "fulfilled")
      .length,
    fallidos: resultados.filter((resultado) => resultado.status === "rejected")
      .length,
  };
};

function AdminLimpieza({ setMensaje, onLimpiezaCompletada }) {
  const [conteos, setConteos] = useState({});
  const [cargandoConteos, setCargandoConteos] = useState(false);
  const [limpiando, setLimpiando] = useState(false);
  const [confirmacion, setConfirmacion] = useState("");
  const [resultado, setResultado] = useState(null);

  const cargarConteos = async () => {
    setCargandoConteos(true);

    try {
      const nuevosConteos = {};

      for (const config of COLECCIONES_A_LIMPIAR) {
        const docs = await obtenerDocsColeccion(config.nombre);
        nuevosConteos[config.nombre] = docs.length;
      }

      setConteos(nuevosConteos);
    } catch (error) {
      console.error("Error cargando conteos de limpieza:", error);
      setMensaje?.("❌ Error al cargar datos de limpieza.");
    } finally {
      setCargandoConteos(false);
    }
  };

  useEffect(() => {
    cargarConteos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const limpiarDatosDeEsteDispositivo = () => {
    CLAVES_LOCAL_STORAGE.forEach((clave) => {
      localStorage.removeItem(clave);
    });

    setMensaje?.("✅ Datos locales de este dispositivo limpiados.");
  };

  const handleReiniciarTiendaPrueba = async () => {
    if (confirmacion.trim().toUpperCase() !== TEXTO_CONFIRMACION) {
      setMensaje?.(
        `⚠️ Para confirmar escribe exactamente: ${TEXTO_CONFIRMACION}`,
      );
      return;
    }

    const confirmar = window.confirm(
      "Vas a reiniciar la tienda de prueba. Se borrarán productos, imágenes de productos, pedidos, analíticas, promociones, marketing y especiales. Esta acción no se puede deshacer. ¿Continuar?",
    );

    if (!confirmar) return;

    setLimpiando(true);
    setResultado(null);
    setMensaje?.("");

    try {
      const resumen = {
        docsBorrados: {},
        imagenesDetectadas: 0,
        imagenesBorradas: 0,
        imagenesFallidas: 0,
      };

      const imagenesParaBorrar = [];

      for (const config of COLECCIONES_A_LIMPIAR) {
        const docs = await obtenerDocsColeccion(config.nombre);

        if (config.borrarImagenes) {
          const imagenes = extraerImagenesDeDocs(docs, config.camposImagen);
          imagenesParaBorrar.push(...imagenes);
        }

        const resultadoDocs = await borrarDocsFirestore(config.nombre, docs);

        resumen.docsBorrados[config.nombre] = resultadoDocs;
      }

      const imagenesUnicas = Array.from(new Set(imagenesParaBorrar));
      resumen.imagenesDetectadas = imagenesUnicas.length;

      const resultadosImagenes = await Promise.allSettled(
        imagenesUnicas.map((urlImagen) => borrarImagenStorage(urlImagen)),
      );

      resumen.imagenesBorradas = resultadosImagenes.filter(
        (resultadoImagen) =>
          resultadoImagen.status === "fulfilled" && resultadoImagen.value?.ok,
      ).length;

      resumen.imagenesFallidas = resultadosImagenes.filter(
        (resultadoImagen) =>
          resultadoImagen.status === "rejected" ||
          resultadoImagen.value?.ok === false,
      ).length;

      CLAVES_LOCAL_STORAGE.forEach((clave) => {
        localStorage.removeItem(clave);
      });

      setResultado(resumen);
      setConfirmacion("");

      await cargarConteos();

      if (typeof onLimpiezaCompletada === "function") {
        await onLimpiezaCompletada();
      }

      setMensaje?.("✅ Tienda de prueba reiniciada correctamente.");
    } catch (error) {
      console.error("Error reiniciando tienda de prueba:", error);
      setMensaje?.("❌ Error al reiniciar la tienda de prueba.");
    } finally {
      setLimpiando(false);
    }
  };

  return (
    <div
      style={{
        backgroundColor: "#fff",
        border: "1px solid #f3d1d1",
        borderRadius: "16px",
        padding: "20px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
      }}
    >
      <h2
        style={{
          margin: "0 0 8px 0",
          fontSize: "22px",
          fontWeight: "900",
          color: "#8b0000",
        }}
      >
        🧹 Limpieza de prueba
      </h2>

      <p
        style={{
          margin: "0 0 18px 0",
          fontSize: "14px",
          color: "#666",
          lineHeight: "1.5",
        }}
      >
        Usa esta sección para reiniciar la tienda antes de cargar el menú real.
        Se borrarán productos, pedidos, analíticas, promociones, marketing y
        especiales, pero se conservará la configuración importante del negocio.
      </p>

      <div
        style={{
          backgroundColor: "#fff8e1",
          border: "1px solid #ffe0a3",
          borderRadius: "14px",
          padding: "14px",
          marginBottom: "18px",
        }}
      >
        <h3
          style={{
            margin: "0 0 10px 0",
            fontSize: "16px",
            fontWeight: "900",
            color: "#7a4b00",
          }}
        >
          Se borrará
        </h3>

        <ul
          style={{
            margin: 0,
            paddingLeft: "18px",
            fontSize: "13px",
            color: "#7a4b00",
            lineHeight: "1.7",
            fontWeight: "700",
          }}
        >
          <li>Productos y sus imágenes en Storage.</li>
          <li>Categorías y orden configurado del menú.</li>
          <li>Pedidos y analíticas de ventas.</li>
          <li>Promociones.</li>
          <li>Marketing / banners.</li>
          <li>Especiales del Mes.</li>
          <li>Carrito, favoritos y último pedido de este dispositivo.</li>
        </ul>
      </div>

      <div
        style={{
          backgroundColor: "#e8f5e9",
          border: "1px solid #c8e6c9",
          borderRadius: "14px",
          padding: "14px",
          marginBottom: "18px",
        }}
      >
        <h3
          style={{
            margin: "0 0 10px 0",
            fontSize: "16px",
            fontWeight: "900",
            color: "#1b5e20",
          }}
        >
          No se borrará
        </h3>

        <ul
          style={{
            margin: 0,
            paddingLeft: "18px",
            fontSize: "13px",
            color: "#1b5e20",
            lineHeight: "1.7",
            fontWeight: "700",
          }}
        >
          <li>Admins.</li>
          <li>Configuración del negocio.</li>
          <li>Logo del negocio.</li>
          <li>Coordenadas y costos de envío.</li>
          <li>Decoraciones.</li>
          <li>Funciones, Telegram y Mercado Pago.</li>
        </ul>
      </div>

      <div
        style={{
          backgroundColor: "#fafafa",
          border: "1px solid #eee",
          borderRadius: "14px",
          padding: "14px",
          marginBottom: "18px",
        }}
      >
        <h3
          style={{
            margin: "0 0 12px 0",
            fontSize: "16px",
            fontWeight: "900",
          }}
        >
          Conteo actual
        </h3>

        {cargandoConteos ? (
          <p style={{ margin: 0, fontSize: "13px", color: "#666" }}>
            Cargando datos...
          </p>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "10px",
            }}
          >
            {COLECCIONES_A_LIMPIAR.map((config) => (
              <div
                key={config.nombre}
                style={{
                  backgroundColor: "#fff",
                  border: "1px solid #eee",
                  borderRadius: "12px",
                  padding: "12px",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    color: "#666",
                    marginBottom: "4px",
                    fontWeight: "700",
                  }}
                >
                  {config.etiqueta}
                </div>

                <div
                  style={{
                    fontSize: "22px",
                    fontWeight: "900",
                    color: "#111",
                  }}
                >
                  {conteos[config.nombre] || 0}
                </div>
              </div>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={cargarConteos}
          disabled={cargandoConteos || limpiando}
          style={{
            marginTop: "12px",
            backgroundColor: "#eee",
            color: "#111",
            border: "none",
            borderRadius: "10px",
            padding: "10px 14px",
            fontSize: "13px",
            fontWeight: "800",
            cursor: cargandoConteos || limpiando ? "not-allowed" : "pointer",
          }}
        >
          Actualizar conteo
        </button>
      </div>

      <div
        style={{
          backgroundColor: "#fff4f4",
          border: "1px solid #ffcdd2",
          borderRadius: "14px",
          padding: "14px",
          marginBottom: "18px",
        }}
      >
        <h3
          style={{
            margin: "0 0 8px 0",
            fontSize: "16px",
            fontWeight: "900",
            color: "#8b0000",
          }}
        >
          Confirmación obligatoria
        </h3>

        <p
          style={{
            margin: "0 0 12px 0",
            fontSize: "13px",
            color: "#8b0000",
            lineHeight: "1.5",
            fontWeight: "700",
          }}
        >
          Para reiniciar la tienda escribe exactamente:
          <br />
          <strong>{TEXTO_CONFIRMACION}</strong>
        </p>

        <input
          type="text"
          value={confirmacion}
          onChange={(e) => setConfirmacion(e.target.value)}
          disabled={limpiando}
          placeholder={TEXTO_CONFIRMACION}
          style={{
            width: "100%",
            padding: "12px",
            borderRadius: "10px",
            border: "1px solid #ddd",
            marginBottom: "12px",
            fontSize: "14px",
            boxSizing: "border-box",
          }}
        />

        <button
          type="button"
          onClick={handleReiniciarTiendaPrueba}
          disabled={
            limpiando ||
            confirmacion.trim().toUpperCase() !== TEXTO_CONFIRMACION
          }
          style={{
            width: "100%",
            backgroundColor:
              limpiando ||
              confirmacion.trim().toUpperCase() !== TEXTO_CONFIRMACION
                ? "#bbb"
                : "#b00020",
            color: "#fff",
            border: "none",
            borderRadius: "12px",
            padding: "14px",
            fontSize: "15px",
            fontWeight: "900",
            cursor:
              limpiando ||
              confirmacion.trim().toUpperCase() !== TEXTO_CONFIRMACION
                ? "not-allowed"
                : "pointer",
          }}
        >
          {limpiando ? "Reiniciando tienda..." : "Reiniciar tienda de prueba"}
        </button>
      </div>

      <div
        style={{
          backgroundColor: "#f7f7f7",
          border: "1px solid #eee",
          borderRadius: "14px",
          padding: "14px",
          marginBottom: resultado ? "18px" : 0,
        }}
      >
        <h3
          style={{
            margin: "0 0 8px 0",
            fontSize: "16px",
            fontWeight: "900",
          }}
        >
          Limpiar solo este dispositivo
        </h3>

        <p
          style={{
            margin: "0 0 12px 0",
            fontSize: "13px",
            color: "#666",
            lineHeight: "1.5",
          }}
        >
          Esto solo borra carrito, favoritos y último pedido guardados en este
          navegador o app instalada. No borra datos de Firestore.
        </p>

        <button
          type="button"
          onClick={limpiarDatosDeEsteDispositivo}
          disabled={limpiando}
          style={{
            backgroundColor: "#111",
            color: "#fff",
            border: "none",
            borderRadius: "10px",
            padding: "11px 14px",
            fontSize: "13px",
            fontWeight: "800",
            cursor: limpiando ? "not-allowed" : "pointer",
          }}
        >
          Limpiar datos de este dispositivo
        </button>
      </div>

      {resultado && (
        <div
          style={{
            backgroundColor: "#e6f4ea",
            border: "1px solid #c8e6c9",
            borderRadius: "14px",
            padding: "14px",
          }}
        >
          <h3
            style={{
              margin: "0 0 10px 0",
              fontSize: "16px",
              fontWeight: "900",
              color: "#137333",
            }}
          >
            Resultado de limpieza
          </h3>

          <ul
            style={{
              margin: 0,
              paddingLeft: "18px",
              fontSize: "13px",
              color: "#137333",
              lineHeight: "1.7",
              fontWeight: "700",
            }}
          >
            {COLECCIONES_A_LIMPIAR.map((config) => (
              <li key={config.nombre}>
                {config.etiqueta}:{" "}
                {resultado.docsBorrados?.[config.nombre]?.borrados || 0}{" "}
                documento(s) borrado(s).
              </li>
            ))}

            <li>
              Imágenes detectadas en Storage: {resultado.imagenesDetectadas}
            </li>
            <li>Imágenes borradas: {resultado.imagenesBorradas}</li>
            <li>Imágenes no borradas: {resultado.imagenesFallidas}</li>
          </ul>
        </div>
      )}
    </div>
  );
}

export default AdminLimpieza;
